import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Card,
  SimpleGrid,
  Badge,
  List,
  ThemeIcon,
  Alert,
  Divider,
  Box,
} from '@mantine/core';
import {
  IconBolt,
  IconSun,
  IconBatteryCharging,
  IconHome,
  IconCalculator,
  IconCheck,
  IconChartLine,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { Layout } from '../shared/components/Layout';
import { useSEO } from '../shared/hooks/useSEO';
import { getOSDMetadata } from '../utils/osdMetadata';
import { getTariffDescription } from '../data/tariffs/tariffDescriptions';
import { TariffType } from '../types/graphql';

export function TariffG13sPage() {
  const osdMetadata = getOSDMetadata('TAURON');
  const tariff = getTariffDescription(TariffType.G13s);

  useSEO({
    title: `G13s - Taryfa dynamiczna OZE TAURON 2026 | Fotowoltaika i Bateria`,
    description:
      'Taryfa G13s TAURON 2026 to najbardziej zaawansowana taryfa dla prosumentów z fotowoltaiką. ' +
      'Najniższe stawki w godzinach produkcji słonecznej (9-17 lato), oszczędz do 40%. ' +
      'Sprawdź czy G13s jest dla Ciebie idealna.',
    canonicalPath: '/TAURON/kalkulatory/podstawowy/G13s',
  });

  return (
    <Layout isAuthenticated={false}>
      {/* Hero Section with SEO Keywords */}
      <Box style={{ backgroundColor: 'var(--mantine-color-lime-0)', padding: '3rem 0' }}>
        <Container size="xl">
          <Stack gap="md" align="center" ta="center">
            <ThemeIcon size={80} radius="xl" variant="light" color="lime">
              <IconSun size={48} />
            </ThemeIcon>
            <Group gap="sm">
              <Badge color="lime" size="lg" variant="filled">
                G13s
              </Badge>
              <Badge color="green" size="md" variant="light">
                NOWOŚĆ VII 2025
              </Badge>
            </Group>
            <Title order={1} size={42} fw={700}>
              G13s - Taryfa Dynamiczna OZE
            </Title>
            <Text size="lg" c="dimmed" maw={800}>
              Najtańsza energia w godzinach produkcji OZE dla prosumentów TAURON.
              Oszczędz do 40% na rachunkach za prąd.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Key Benefits */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <Title order={2} mb="md">
            Dlaczego G13s to najlepszy wybór dla fotowoltaiki?
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <ThemeIcon size={40} radius="md" variant="light" color="lime">
                <IconBatteryCharging size={24} />
              </ThemeIcon>
              <Text fw={600} mt="sm">
                Najniższe stawki słoneczne
              </Text>
              <Text size="sm" c="dimmed" mt="xs">
                Cena około 0,20 zł/kWh w godzinach produkcji (9-17 w sezonie letnim)
              </Text>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <ThemeIcon size={40} radius="md" variant="light" color="lime">
                <IconBolt size={24} />
              </ThemeIcon>
              <Text fw={600} mt="sm">
                Samowystarczająca energii
              </Text>
              <Text size="sm" c="dimmed" mt="xs">
                Wykorzystaj 100% energii ze swojego systemu PV
              </Text>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <ThemeIcon size={40} radius="md" variant="light" color="lime">
                <IconHome size={24} />
              </ThemeIcon>
              <Text fw={600} mt="sm">
                Idealna z baterią domową
              </Text>
              <Text size="sm" c="dimmed" mt="xs">
                Magazynowanie nadwyżek energii na czas nocny
              </Text>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <ThemeIcon size={40} radius="md" variant="light" color="lime">
                <IconCalculator size={24} />
              </ThemeIcon>
              <Text fw={600} mt="sm">
                Prosta kalkulacja
              </Text>
              <Text size="sm" c="dimmed" mt="xs">
                Automatyczne rozliczanie na podstawie licznika
              </Text>
            </Card>
          </SimpleGrid>
        </Stack>
      </Container>

      {/* Detailed Tariff Information */}
      <Container size="xl" py={60}>
        <Card shadow="md" padding="xl" radius="lg" withBorder>
          <Stack gap="xl">
            <div>
              <Group gap="xs" mb="md">
                <Badge color="lime" size="lg" variant="filled">
                  G13s
                </Badge>
                <Badge color="green" size="md" variant="light">
                  NOWOŚĆ VII 2025
                </Badge>
              </Group>
              <Title order={2}>{tariff.name}</Title>
            </div>

            <Text size="lg" mb="lg">
              {tariff.fullDescription}
            </Text>

            <Divider />

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <div>
                <Text fw={600} mb="sm" c="lime">
                  Dla kogo idealna:
                </Text>
                <List
                  spacing="xs"
                  size="sm"
                  icon={
                    <ThemeIcon color="lime" size={20} radius="xl">
                      <IconCheck size={12} />
                    </ThemeIcon>
                  }
                >
                  {tariff.bestFor.map((item, index) => (
                    <List.Item key={index}>{item}</List.Item>
                  ))}
                </List>
              </div>

              <div>
                <Text fw={600} mb="sm" c="red">
                  Wymagania:
                </Text>
                <List
                  spacing="xs"
                  size="sm"
                  icon={
                    <ThemeIcon color="red" size={20} radius="xl">
                      <IconCheck size={12} />
                    </ThemeIcon>
                  }
                >
                  <List.Item>Wymaga inteligentnego licznika</List.Item>
                  <List.Item>Wymaga deklaracji systemu PV</List.Item>
                  <List.Item>Wymaga baterii lub magazynu energii</List.Item>
                  <List.Item>Klienci: {osdMetadata.name}</List.Item>
                </List>
              </div>
            </SimpleGrid>

            <Divider />

            <Group justify="space-between" align="flex-start">
              <div>
                <Text size="sm" c="dimmed" mb="xs">
                  Strefy czasowe:
                </Text>
                <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
                  {tariff.timeZoneInfo}
                </Text>
              </div>
              <div>
                <Text size="sm" c="dimmed" mb="xs">
                  Potencjalne oszczędności:
                </Text>
                <Text size="lg" fw={700} c="lime">
                  {tariff.estimatedSavings}
                </Text>
              </div>
            </Group>

            <Alert icon={<ThemeIcon size={20} color="lime"><IconChartLine size={12} /></ThemeIcon>} color="lime" variant="light">
              <Text size="sm">
                <strong>Informacja:</strong> Taryfa G13s wymaga inteligentnego licznika
                ({osdMetadata.elicznikPortal.name}). Sprawdź dostępność u dostawcy.
              </Text>
            </Alert>
          </Stack>
        </Card>
      </Container>

      {/* Time Zone Visualization */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Strefy czasowe G13s - kiedy prąd jest najtańszy?
            </Title>
            <Text c="dimmed">
              Poznaj, w których godzinach energia słoneczna kosztuje najmniej.
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm" c="lime">
                Sezon letni (IV-IX)
              </Title>
              <Alert color="lime" variant="filled">
                <Text size="md" fw={700} c="white">
                  ⚡ Najtańsza: 9:00 - 17:00
                </Text>
                <Text size="sm" c="white">
                  W tych godzinach wykorzystaj całą energię z PV
                </Text>
              </Alert>
              <Text size="sm" mt="md" c="dimmed">
                Stawka: ~0,20 zł/kWh (cena rynkowa energii z baterii)
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm" c="orange">
                Sezon zimowy (X-III)
              </Title>
              <Alert color="orange" variant="light">
                <Text size="md" fw={700}>
                  Szczyty: 7:00-9:00, 17:00-21:00
                </Text>
                <Text size="sm" c="dimmed">
                  Unikaj szczyty energetyczne (rano i wieczorem)
                </Text>
              </Alert>
              <Text size="sm" mt="md" c="dimmed">
                Stawka: tania strefa, ale wyższa niż lato
              </Text>
            </Card>
          </SimpleGrid>

          <Card shadow="md" padding="lg" radius="md" withBorder>
            <Title order={3} mb="sm">
              Noc (całoroczna)
            </Title>
            <Alert color="indigo" variant="light">
              <Text size="md" fw={700}>
                Noc: 22:00 - 7:00
              </Text>
              <Text size="sm" c="dimmed">
                Oszczędna strefa - idealna do ładowania samochodu z baterii
              </Text>
            </Alert>
          </Card>
        </Stack>
      </Container>

      {/* Comparison with Other Tariffs */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Porównanie G13s z innymi taryfami TAURON
            </Title>
            <Text c="dimmed">
              Zobacz, jak G13s wypada na tle innych taryf energetycznych.
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder style={{ opacity: 0.7 }}>
              <Title order={4} mb="sm">
                G11 - Jednostrefowa
              </Title>
              <Text size="sm" c="dimmed">
                Jedna stała stawka 24h. Brak możliwości oszczędności.
              </Text>
              <Text size="lg" fw={700} mt="md" c="gray">
                Brak oszczędności
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder style={{ opacity: 0.8 }}>
              <Title order={4} mb="sm">
                G12 - Dwustrefowa
              </Title>
              <Text size="sm" c="dimmed">
                Tańsza noc (22:00-6:00). Oszczędz do 20%.
              </Text>
              <Text size="lg" fw={700} mt="md" c="blue">
                Do 20%
              </Text>
            </Card>

            <Card shadow="md" padding="lg" radius="md" withBorder style={{ borderWidth: 3, borderColor: 'var(--mantine-color-lime-5)' }}>
              <Group gap="xs" mb="sm">
                <Badge color="lime" variant="filled">
                  G13s - Dynamiczna OZE
                </Badge>
                <Badge color="green" variant="light">
                  REKOMENDACJA
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                Najtańsza w godzinach produkcji PV. Oszczędz do 40%.
              </Text>
              <Text size="lg" fw={700} mt="md" c="lime">
                Do 40%
              </Text>
            </Card>
          </SimpleGrid>
        </Stack>
      </Container>

      {/* How to Get G13s */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Jak zmienić taryfę na G13s?
            </Title>
            <Text c="dimmed">
              Proces zmiany taryfy jest prosty i w pełni online.
            </Text>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm">
                Krok 1: Sprawdź dostępność
              </Title>
              <Text size="sm" c="dimmed">
                Zaloguj się do {osdMetadata.elicznikPortal.name} (
                <a
                  href={osdMetadata.elicznikPortal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--mantine-color-lime-6)' }}
                >
                  {osdMetadata.elicznikPortal.url}
                </a>
                ) i sprawdź, czy taryfa G13s jest dostępna w Twoim obszarze.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm">
                Krok 2: Złóż wniosek
              </Title>
              <Text size="sm" c="dimmed">
                Złóż wniosek o zmianę taryfy na G13s online. Zmiana jest zazwycz
                realizowana w ciągu kilku dni roboczych.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm">
                Krok 3: Wymiana licznika
              </Title>
              <Text size="sm" c="dimmed">
                G13s wymaga inteligentnego licznika. Jeśli go nie masz, {osdMetadata.shortName}
                zainstaluje go bezpłatnie.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="sm">
                Krok 4: Ciesz się oszczędnościami
              </Title>
              <Text size="sm" c="dimmed">
                Od następnego rozliczenia płacisz mniej za prąd produkowany z
                Twojej instalacji PV.
              </Text>
            </Card>
          </SimpleGrid>
        </Stack>
      </Container>

      {/* FAQ */}
      <Container size="xl" py={60}>
        <Stack gap="xl">
          <div>
            <Title order={2} mb="md">
              Często zadawane pytania o G13s
            </Title>
          </div>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} mb="sm">
                Czy G13s jest dostępna w całym kraju?
              </Text>
              <Text size="sm" c="dimmed">
                Nie, G13s jest dostępna tylko u TAURON Dystrybucja. Jeśli jesteś
                klientem innego operatora, sprawdź dostępne alternatywy.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} mb="sm">
                Ile mogę zaoszczędzić z G13s?
              </Text>
              <Text size="sm" c="dimmed">
                Oszczędności zależą od profilu zużycia i mocy instalacji.
                Typowo 30-40% dla prosumentów z baterią domową i intensywnym
                zużyciem w dzień.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} mb="sm">
                Czy mogę mieć G13s bez baterii?
              </Text>
              <Text size="sm" c="dimmed">
                G13s jest dedykowana głównie dla prosumentów z baterią domową lub
                magazynowaniem energii. Bez magazynu oszczędności będą mniejsze.
              </Text>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} mb="sm">
                Jak działa kalkulacja w G13s?
              </Text>
              <Text size="sm" c="dimmed">
                Ceny zmieniają się co godzinę na podstawie strefy czasowej i Twojego
                zużycia. Licznik inteligentny automatycznie rozlicza po odpowiedniej
                stawce.
              </Text>
            </Card>
          </SimpleGrid>
        </Stack>
      </Container>

      {/* CTA */}
      <Box style={{ backgroundColor: 'var(--mantine-color-lime-6)', color: 'white' }} py={60}>
        <Container size="md">
          <Stack gap="xl" align="center" ta="center">
            <ThemeIcon size={80} radius="xl" variant="white" color="lime">
              <IconCalculator size={48} />
            </ThemeIcon>
            <Title order={2} c="white">
              Sprawdź, ile zaoszczędzisz z G13s
            </Title>
            <Text size="lg" c="white" maw={700}>
              Użyj naszego kalkulatora, aby porównać koszty G13s z innymi taryfami
              na podstawie Twojego rzeczywistego zużycia energii.
            </Text>
            <Button
              size="xl"
              variant="white"
              color="lime"
              component={Link}
              to="/TAURON/kalkulatory/podstawowy"
              leftSection={<IconCalculator size={24} />}
            >
              Przejdź do kalkulatora
            </Button>
          </Stack>
        </Container>
      </Box>
    </Layout>
  );
}
